<template>
  <div
    class="relative mb-4"
    @selectstart="onSelectStart"
    :style="{ height: height + 'px' }"
  >
    <!-- This element is used to calculate the item size and subsequently the grid layout -->
    <div
      class="invisible absolute pointer-events-none inline-block"
      style="line-height:0"
    >
      <slot name="reference"></slot>
    </div>
    <!-- Placeholders -->
    <div
      v-for="(entry, index) in entries"
      :key="'placeholder-' + index"
      class="item-slot-placeholder relative inline-block"
      :style="{
        transform: 'translate(' + entry.x + 'px, ' + entry.y + 'px)'
      }"
    >
      <template
        v-if="
          showAddItemButton &&
            parseInt(index) === entries.findIndex(c => c.item === null)
        "
        ><slot name="additem" v-bind:entry="entry"></slot
      ></template>
      <!--
      <router-link
        :to="{ name: 'match' }"
        draggable="false"
      >
        <item-placeholder
          :interactable="true"
          :small="itemSize === 'small'"
        ></item-placeholder>
      </router-link>-->
      <template v-else-if="entry.itemId == null || dropMode === 'replace'"
        ><slot name="placeholder" v-bind:entry="entry"></slot
      ></template>
      <!--
      <item-placeholder
        v-else-if="entry.itemId == null || dropMode === 'replace'"
        :interactable="dropMode === 'replace'"
        :small="itemSize === 'small'"
        @click="onEmptySlotInteract"
      ></item-placeholder>-->
    </div>
    <!-- Item Entries -->
    <div
      v-for="entry in entries"
      :key="entry.uid"
      style="overflow: visible;"
      class="item-slot relative"
      :style="{
        transform: 'translate(' + entry.x + 'px, ' + entry.y + 'px)'
      }"
      :class="{
        'item-position-animated': enableAnimations,
        'pointer-events-none': draggingItem
      }"
      draggable="false"
    >
      <div
        v-if="entry.item != null"
        draggable="false"
        @click="onItemClick({ item: entry.item })"
      >
        <slot
          name="item"
          v-bind:entry="entry"
          v-bind:draggable="dragMode != null"
          v-bind:cloneOnDrag="dragMode === 'clone'"
        />
        <!--
        <item-icon
          :uid="entry.uid"
          :item-id="entry.itemId"
          :active-list="activeList"
          :show-name="true"
          :show-match-percent="true"
          :show-position="true"
          :position="entry.rank != null ? entry.rank : 0"
          :itemDraggable="dragMode != null"
          :clone-on-drag="dragMode === 'clone'"
          :small="itemSize === 'small'"
          @itemdragstart="onItemDragStart"
        ></item-icon>-->
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { ViewportHelper } from "@/helpers/viewport-helper";

/**
 * @type DraggableItemGrid
 * Arranges a collection of items in a grid that, depending on the settings, can be interacted with in various
 * ways.
 * Events:
 * dragstart: Triggered when the user starts dragging a item
 * itemdrop: Triggered when the user drops a item on the grid
 * placeholderclick: Triggered when the user interacts with a placeholder
 */
export default {
  name: "DraggableItemGrid",
  props: {
    /**
     * @var The items in their correct order, with null for empty slots
     */
    items: {
      type: Array,
      required: true
    },
    /**
     * @var Drag bus for inter component communication of drag and drop actions
     */
    dragBus: {
      type: Vue,
      default: null
    },
    /**
     * @var One of: null|"clone"|"drag"
     */
    dragMode: {
      type: String,
      default: null
    },
    /**
     * @var One of: null|"insert"|"replace"
     */
    dropMode: {
      type: String,
      default: null
    },
    /**
     * @var Used to determine which drop target will be interacted with when the drag is released. A higher value
     * means a higher priority.
     */
    dragInteractionPriority: {
      type: Number,
      default: 0
    },
    /**
     * @description If assigned, clamps the area where interactions are possible. E.g. to prevent drops outside of the
     * viewport of a surrounding scroll area.
     * @template [top, right, bottom, left]
     */
    dropBounds: {
      type: Array,
      default: null
    },
    dragAreaGroup: {
      type: String,
      default: null
    },
    /**
     * @var Whether to show the additem element
     */
    showAddItemButton: {
      type: Boolean,
      default: false
    },
    allowDropOnEmptySlot: {
      type: Boolean,
      default: false
    },
    enableAnimations: {
      type: Boolean,
      default: true
    },
    horizontalAlign: {
      type: String,
      default: "center"
    }
  },
  data: () => {
    return {
      entries: [],
      gridArea: null,
      editorAreaWidth: 0,
      entryWidth: 0,
      entryHeight: 0,
      entrySpacingX: 0,
      entrySpacingY: 0,
      columns: 0, // amount of columns, needed to calculate drag interactions and the layout
      rows: 0, // amount of rows, needed to calculate drag interactions
      height: 0,
      contentOffsetLeft: 0, // slot grid is centered, this value represents the left offset of the grid
      draggingItem: false,
      draggedItemIndex: -1
    };
  },
  created() {
    window.addEventListener("resize", this.onWindowResize);
    if (this.dragBus) {
      this.dragBus.$on("dragstart", this.onItemDragStartForeign); // listen to drag start events from other areas
      this.dragBus.$on("drag", this.onItemDrag); // listen to drag events to perform drag interactions
      this.dragBus.$on("dragend", this.onItemDragEnd); // listen to drag end events to suggest drop interactions
      this.dragBus.$on("drop", this.onItemDrop); // listen to drop events to perform drop interactions
    }
    this.$on("itemdragstart", this.onItemDragStart);
  },
  mounted() {
    this.gridArea = this.$el;
    const computedTemplateStyle = getComputedStyle(this.gridArea.children[0]);
    this.entryWidth = parseInt(computedTemplateStyle.width.replace("px", ""));
    this.entryHeight = parseInt(computedTemplateStyle.height.replace("px", ""));
    this.entrySpacingX = parseInt(
      computedTemplateStyle.marginRight.replace("px", "")
    );
    this.entrySpacingY = parseInt(
      computedTemplateStyle.marginBottom.replace("px", "")
    );
    this.entries = [];
    this.updateEntries();
    this.onWindowResize();
  },
  destroyed() {
    window.removeEventListener("resize", this.onWindowResize);
  },
  watch: {
    items() {
      const previousRows = this.rows;
      this.updateEntries();
      if (previousRows <= 1) {
        this.onWindowResize();
      }
      this.updateLayout();
    }
  },
  methods: {
    onWindowResize() {
      const grid = [...this.gridArea.children];
      if (grid.length === 0) {
        this.columns = 0;
        this.updateLayout();
        return;
      }
      const areaWidth = parseInt(
        getComputedStyle(this.gridArea).width.replace("px", "")
      );
      this.editorAreaWidth = areaWidth;
      this.columns = Math.min(
        this.entries.length,
        Math.floor(
          (areaWidth + this.entrySpacingX) /
            (this.entryWidth + this.entrySpacingX)
        )
      );
      this.updateLayout();
    },
    onSelectStart(event) {
      event.stopPropagation();
      event.preventDefault();
    },
    onItemClick(event) {
      this.$emit("itemclick", event.item);
    },
    onItemDragStart(event) {
      event.source = this;
      this.dragBus.$emit("dragstart", event);
      this.draggingItem = true;
      this.draggedItemIndex = this.entries.findIndex(e => e === event.entry);
    },
    onItemDragStartForeign(event) {
      if (
        event.source === this ||
        event.source.dragAreaGroup !== this.dragAreaGroup
      ) {
        return;
      }
      this.draggingItem = true;
      this.draggedItemIndex = -1;
    },
    onItemDrag(event) {
      if (event.source.dragAreaGroup !== this.dragAreaGroup) {
        return;
      }
      switch (this.dropMode) {
        case "insert":
          this.handleItemInsert(event);
          break;
        case "replace":
          // this.handleItemReplace(event);
          break;
        default:
          break;
      }
    },
    onItemDragEnd(event) {
      if (event.source.dragAreaGroup !== this.dragAreaGroup) {
        return;
      }
      if (this.dropMode != null) {
        event.targets.push({
          target: this,
          name: "item-slot-grid",
          priority: this.dragInteractionPriority
        });
      }

      setTimeout(() => {
        this.draggingItem = false;
      }, 5);
    },
    onItemDrop(event) {
      switch (this.dropMode) {
        case "insert":
          if (event.target !== this) {
            return;
          }
          this.$emit("itemdrop", {
            itemId: event.itemId,
            index: this.draggedItemIndex
          });
          break;
        case "replace": {
          const draggedIndex = this.draggedItemIndex;
          const dropIndex = this.handleItemReplace(event);
          const dropEvent = {
            itemId: event.itemId,
            index: dropIndex >= 0 ? dropIndex : null,
            oldIndex: draggedIndex >= 0 ? draggedIndex : null,
            cancelled: false
          };
          this.$emit("itemdrop", dropEvent);
          if (dropEvent.cancelled) {
            this.undoItemReplace(dropEvent);
          }
          break;
        }
        default:
          break;
      }
    },
    onEmptySlotInteract(event) {
      this.$emit("placeholderclick", event);
    },
    handleItemInsert(event) {
      const index = this.getEntryIndex(
        event.x,
        event.y,
        event.clientX,
        event.clientY
      );
      const oldIndex = this.draggedItemIndex;
      if (
        index === oldIndex ||
        index < 0 ||
        index >= this.items.length ||
        (!this.allowDropOnEmptySlot && this.items[index] == null)
      ) {
        return;
      }

      const entry = event.entry;
      // console.log("old: " + oldIndex + ", new: " + index);
      this.entries.splice(oldIndex, 1);
      this.entries.splice(index, 0, entry);
      this.draggedItemIndex = index;

      this.applyEntriesToItems();

      setTimeout(() => {
        this.updateLayout();
      }, 1);
    },
    handleItemReplace(event) {
      const index =
        event.target === this
          ? this.getEntryIndex(event.x, event.y, event.clientX, event.clientY)
          : -1;
      const oldIndex = this.draggedItemIndex;
      const draggedFromOutside = this.draggedItemIndex < 0;
      const droppedOutside =
        index < 0 ||
        index >= this.items.length ||
        (!this.allowDropOnEmptySlot && this.items[index] == null);
      if (draggedFromOutside && droppedOutside) {
        return;
      }

      const entryA = oldIndex >= 0 ? this.getEntryAt(oldIndex) : null;
      const entryB = index >= 0 ? this.getEntryAt(index) : null;
      const itemA = event.item;
      const itemB = entryB ? entryB.item : null;
      if (entryA && entryB && this.enableAnimations) {
        this.entries[index] = this.createEntry();
        this.entries[index].item = itemA;
        this.entries[index].x = entryB.x;
        this.entries[index].y = entryB.y;
        this.entries[oldIndex] = entryB;
      } else {
        if (entryA) {
          entryA.item = itemB;
        }
        if (entryB) {
          entryB.item = itemA;
        }
      }

      this.applyEntriesToItems();

      if (this.enableAnimations) {
        setTimeout(() => {
          this.updateLayout();
        }, 50);
      } else {
        this.updateLayout();
      }

      return index;
    },
    undoItemReplace(event) {
      if (event.index >= 0) {
        this.entries[event.index].item = null;
        this.applyEntriesToItems();
      }
      if (!this.enableAnimations) {
        this.updateLayout();
      }
    },
    applyEntriesToItems() {
      for (let i = 0; i < this.items.length; i++) {
        this.items[i] = this.getEntryAt(i).item;
      }
    },
    updateEntries() {
      const items = this.items;
      const desiredEntriesCount =
        this.items.length +
        (this.showAddItemButton &&
        this.items.findIndex(itemId => itemId === null) < 0
          ? 1
          : 0);
      if (this.entries.length !== desiredEntriesCount) {
        this.entries.length = desiredEntriesCount;
      }
      for (let i = 0; i < this.entries.length; i++) {
        if (this.entries[i] != null) {
          this.entries[i].item = null;
          continue;
        }
        this.entries[i] = this.createEntry();
      }

      for (let i = 0; i < items.length; i++) {
        this.entries[i].item = items[i];
      }
    },
    updateLayout() {
      const entryWidth = this.entryWidth;
      const entryHeight = this.entryHeight;
      const entrySpacingX = this.entrySpacingX;
      const entrySpacingY = this.entrySpacingY;
      const entriesCount = this.entries.length;
      const columns = this.columns;
      const rows = columns > 0 ? Math.ceil(entriesCount / columns) : 0;

      const contentWidth = Math.max(
        0,
        columns * entryWidth + (columns - 1) * entrySpacingX
      );
      const contentOffsetLeft = this.getContentLeftOffset(
        this.editorAreaWidth,
        contentWidth
      );

      this.rows = rows;
      this.height = rows * this.entryHeight + (rows - 1) * this.entrySpacingY;
      this.contentOffsetLeft = contentOffsetLeft;
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < columns; x++) {
          const index = x + y * columns;
          if (index >= this.entries.length) {
            break;
          }

          const entry = this.getEntryAt(index);
          entry.x = contentOffsetLeft + x * entryWidth + x * entrySpacingX;
          entry.y = y * entryHeight + y * entrySpacingY;
        }
      }
      this.$forceUpdate();
    },
    createEntry() {
      return {
        uid: this.$uuid.v4(),
        item: null,
        x: 0,
        y: 0
      };
    },
    getContentLeftOffset(areaWidth, contentWidth) {
      switch (this.horizontalAlign) {
        case "left":
          return 0;
        case "center":
          return (areaWidth - contentWidth) / 2;
        case "right":
          return areaWidth - contentWidth;
        default: {
          console.warn(
            "Unknown horizontal align for draggable item grid supplied: " +
              this.horizontalAlign
          );
          return 0;
        }
      }
    },
    getEntryAt(index) {
      return this.entries[index];
    },
    getEntryIndex(x, y, clientX, clientY) {
      if (this.dropBounds) {
        const top = this.dropBounds[0];
        const right = this.dropBounds[1];
        const bottom = this.dropBounds[2];
        const left = this.dropBounds[3];
        if (
          clientX < left ||
          clientY < top ||
          clientX > right ||
          clientY > bottom
        ) {
          return -1;
        }
      }
      const gridArea = this.gridArea;
      const editorAreaPosition = ViewportHelper.getElementViewportPosition(
        gridArea
      );
      const entryWidth = this.entryWidth;
      const entryHeight = this.entryHeight;
      const entrySpacingX = this.entrySpacingX;
      const entrySpacingY = this.entrySpacingY;
      const posX = editorAreaPosition.x + this.contentOffsetLeft;
      const posY = editorAreaPosition.y;
      const relativePosX = x - posX;
      const relativePosY = y - posY;

      const slotX = Math.floor(
        (relativePosX + (entryWidth + entrySpacingX) / 2) /
          (entryWidth + entrySpacingX)
      );
      const slotY = Math.floor(
        (relativePosY + (entryHeight + entrySpacingY) / 2) /
          (entryHeight + entrySpacingY)
      );
      return slotX >= 0 &&
        slotX < this.columns &&
        slotY >= 0 &&
        slotY < this.rows
        ? slotX + slotY * this.columns
        : -1;
    }
  }
};
</script>

<style scoped>
.item-slot,
.item-slot-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  line-height: 0;
}
.item-position-animated {
  transition: transform 0.1s ease-out;
}
</style>
